import { useQuery } from '@tanstack/react-query'
import { Check, Dot } from 'lucide-react'
import { RULES } from '@sdg/shared'
import { Badge, Card, CardContent } from '../components/ui/card'
import { get } from '../lib/api'

interface Counts {
  booths: number
  judges: number
  rubrics: number
  rubricKpis: number
  luckyDraws: number
  scoreSheets: number
  expectedScoreSheets: number
  staffAccounts: number
  students: number
  boothScans: number
  certificates: number
}

interface Health {
  status: string
  database: 'up' | 'down'
  latencyMs: number
  mailDriver: string
}

/** Operational check used during the build and the 14 Oct dry run. */
export default function SystemCheck() {
  const health = useQuery({
    queryKey: ['health'],
    queryFn: () => get<Health>('/health'),
    refetchInterval: 10_000,
  })
  const counts = useQuery({ queryKey: ['counts'], queryFn: () => get<Counts>('/api/meta/counts') })
  const c = counts.data

  return (
    <>
      <div className="mb-5">
        <p className="mb-1.5 text-[11px] font-bold uppercase tracking-[0.13em] text-teal">
          Operations
        </p>
        <h1 className="text-[22px] font-bold leading-tight text-navy-950">System check</h1>
        <p className="mt-1.5 text-[13.5px] text-muted">
          Live wiring and the reference data transcribed from the Committee proposal.
        </p>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <Stat value={c?.students} label="Registered" />
        <Stat value={c?.boothScans} label="Booth scans" />
        <Stat value={c?.certificates} label="Certificates" />
        <Stat value={c?.scoreSheets} label="Score sheets" />
      </div>

      <Card>
        <CardContent>
        <Row
          label="API server"
          ok={!health.isError && !!health.data}
          value={health.data ? `ok · ${health.data.latencyMs}ms` : 'unreachable'}
        />
        <Row label="MySQL database" ok={health.data?.database === 'up'} value={health.data?.database ?? '…'} />
        <Row
          label="Rubrics & KPIs"
          ok={c?.rubrics === 3 && c?.rubricKpis === 21}
          value={c ? `${c.rubrics} rubrics · ${c.rubricKpis} KPIs` : '…'}
        />
        <Row
          label="Score sheets seeded"
          ok={c?.scoreSheets === c?.expectedScoreSheets}
          value={c ? `${c.scoreSheets} / ${c.expectedScoreSheets}` : '…'}
        />
        <Row
          label="Staff accounts"
          ok={c?.staffAccounts === 18}
          value={c ? `${c.staffAccounts} (1 admin · 14 booth · 3 judge)` : '…'}
        />
        <Row label="Mail driver" ok={!!health.data?.mailDriver} value={health.data?.mailDriver ?? '…'} last />
        </CardContent>
      </Card>

      <Card className="mt-3 border-none bg-navy-950 text-white">
        <CardContent>
        <h2 className="mb-2 font-bold">Event rules in force</h2>
        <ul className="list-disc pl-5 text-[13px] leading-loose">
          <li>
            {RULES.certificateThreshold} of {RULES.totalBooths} booths earns the certificate
          </li>
          <li>{RULES.luckyDrawThreshold} booths enters the lucky draw</li>
          <li>
            {RULES.otp.length}-digit OTP, {RULES.otp.ttlMinutes} min expiry, max{' '}
            {RULES.otp.maxAttempts} attempts
          </li>
          <li>One win per student across all three draws</li>
        </ul>
        </CardContent>
      </Card>
    </>
  )
}

function Stat({ value, label }: { value?: number; label: string }) {
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className="text-[40px] font-bold leading-none text-navy-950 tabular-nums">
        {value ?? '—'}
      </div>
      <div className="mt-1 text-[11px] uppercase tracking-wider text-muted">{label}</div>
    </div>
  )
}

function Row({
  label,
  ok,
  value,
  last,
}: {
  label: string
  ok: boolean | undefined
  value: string
  last?: boolean
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 py-2.5 ${last ? '' : 'border-b border-line-2'}`}
    >
      <span className="text-[13px] text-muted">{label}</span>
      <Badge tone={ok ? 'green' : 'orange'}>
        {ok ? <Check className="size-3" strokeWidth={3} /> : <Dot className="size-3" />} {value}
      </Badge>
    </div>
  )
}
